/**
 * AnimatedHeroDemo — auto-playing 12s loop for the marketing landing hero.
 *
 * Timeline:
 *   0.0s   Idle dictate card visible, mic dot pulsing
 *   0.5s   Word-by-word typewriter of the caregiver dictation (≈4.5s)
 *   5.0s   "Format with AI" button pulses + spinner
 *   5.8s   AI-formatted note card slides up + fades in
 *   6.0s   Lines of the formatted note stream in (~2.5s)
 *   8.5s   Tag pills pop in (staggered)
 *   9.0s   "Generated in 1.4s" check appears
 *  10.5s   Hold final state
 *  12.0s   Reset → loop
 *
 * Implementation: a single setInterval that advances `tick` (0..240, 50ms
 * cadence = 12s loop). All visibility derived from `tick` to keep state
 * minimal and the animation deterministic.
 */
import { useEffect, useState } from "react";
import { Mic, Sparkles, Check, Loader2 } from "lucide-react";

const DICTATION =
  "Ate breakfast around 8, walked the hallway with the walker, said her left knee was a bit sore after. BP — 128 over 80.";
const DICT_WORDS = DICTATION.split(" ");

const FORMATTED_LINES = [
  "Client tolerated breakfast at ~08:00. Ambulated independently with walker; reported mild left knee discomfort post-ambulation.",
  "Vitals: BP 128/80 mmHg.",
];
const TAGS = ["Meds administered", "Vitals captured", "Ambulation", "Mild knee pain"];

// Loop length in ticks (50ms per tick → 12s)
const LOOP_TICKS = 240;

export default function AnimatedHeroDemo() {
  const [tick, setTick] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setTick((t) => (t + 1) % LOOP_TICKS);
    }, 50);
    return () => clearInterval(id);
  }, [paused]);

  // Phase derivations (1 tick = 50ms)
  // 0-10 ticks (0–500ms): idle
  // 10-100 (0.5s–5s): typewriter
  // 100-116 (5s–5.8s): formatting (spinner)
  // 116+ (5.8s+): formatted card visible
  const wordsShown = Math.max(0, Math.min(DICT_WORDS.length,
    Math.floor((tick - 10) / ((100 - 10) / DICT_WORDS.length))));
  const formatting = tick >= 100 && tick < 116;
  const formattedVisible = tick >= 116;
  const lineCount = formattedVisible
    ? Math.min(FORMATTED_LINES.length, Math.floor((tick - 116) / 25) + 1)
    : 0;
  const tagsVisible = tick >= 170;
  const checkVisible = tick >= 180;

  const dictateTyped = DICT_WORDS.slice(0, wordsShown).join(" ");

  return (
    <div
      className="relative lg:pl-6"
      data-testid="hero-animated-demo"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative">
        {/* ── Back card: caregiver dictation ─────────────── */}
        <div className="rounded-2xl bg-white border border-clinical-border/70 p-5 shadow-[0_30px_80px_-30px_rgba(11,21,48,0.18)]">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-clinical-subtext">
              Your notes
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-afyanex-pink/10 text-afyanex-pink text-[11px] font-semibold px-2.5 py-1">
              <span className={`h-1.5 w-1.5 rounded-full bg-afyanex-pink ${wordsShown < DICT_WORDS.length ? "animate-pulse" : "opacity-30"}`} />
              <Mic className="h-3 w-3" /> {wordsShown < DICT_WORDS.length ? "Recording" : "Done"}
            </span>
          </div>
          <p className="text-[14px] text-clinical-text leading-relaxed min-h-[5.5rem]">
            {dictateTyped}
            {wordsShown < DICT_WORDS.length && (
              <span className="inline-block w-1.5 h-4 bg-afyanex-pink/70 align-middle ml-0.5 animate-pulse" aria-hidden />
            )}
          </p>
          <div className="mt-4 flex items-center justify-between text-[12px] text-clinical-subtext">
            <span>0:{(Math.min(42, Math.floor(tick / 2))).toString().padStart(2, "0")} · drive home</span>
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] font-medium transition-all ${
                formatting
                  ? "bg-afyanex-blue text-white shadow-[0_6px_18px_-6px_rgba(0,94,255,0.55)]"
                  : "bg-afyanex-blue/10 text-afyanex-blue"
              }`}
              aria-label="Format with AI"
            >
              {formatting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
              {formatting ? "Formatting…" : "Format with AI"}
            </button>
          </div>
        </div>

        {/* ── Front card: AI-formatted note ──────────────── */}
        <div
          className={`absolute -bottom-12 -right-2 sm:-right-6 lg:-right-10 w-[88%] sm:w-[85%] rounded-2xl bg-white border border-clinical-border/70 p-5 shadow-[0_30px_80px_-25px_rgba(0,94,255,0.35)] transition-all duration-700 ${
            formattedVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-6 pointer-events-none"
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-afyanex-blue/10 text-afyanex-blue">
                <Sparkles className="h-3.5 w-3.5" />
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-afyanex-blue">
                AI formatted note
              </span>
            </div>
            <span
              className={`inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium transition-opacity duration-300 ${
                checkVisible ? "opacity-100" : "opacity-0"
              }`}
            >
              <Check className="h-3 w-3" /> 1.4s
            </span>
          </div>
          <div className="space-y-2 min-h-[4.5rem]">
            {FORMATTED_LINES.map((ln, i) => {
              const hasBP = ln.includes("BP 128/80");
              const visible = i < lineCount;
              return (
                <p
                  key={i}
                  className={`text-[13.5px] text-clinical-text leading-relaxed transition-all duration-500 ${
                    visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
                  }`}
                >
                  {hasBP ? (
                    <>
                      Vitals: <span className="font-medium">BP 128/80 mmHg</span>.
                    </>
                  ) : (
                    ln
                  )}
                </p>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {TAGS.map((t, i) => (
              <span
                key={t}
                className={`text-[11px] rounded-full bg-afyanex-blue/10 text-afyanex-blue px-2.5 py-1 font-medium transition-all duration-500 ${
                  tagsVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-1"
                }`}
                style={{ transitionDelay: tagsVisible ? `${i * 90}ms` : "0ms" }}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* Hover hint */}
      <div className="hidden lg:block absolute -top-3 right-0 text-[10px] uppercase tracking-[0.18em] text-clinical-subtext/70">
        {paused ? "paused · hover off to play" : "live · hover to pause"}
      </div>
    </div>
  );
}
